import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { EmailTypoHint } from "@/components/EmailTypoHint";
import { api, formatError } from "@/lib/api";

const WarningRow = ({ batchId, w, letter, onChange }) => {
  const [email, setEmail] = useState(letter?.recipient_email || "");
  const [busy, setBusy] = useState(false);
  const call = async (fn) => {
    setBusy(true);
    try {
      onChange((await fn()).data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () => call(() => api.patch(`/bulk/batches/${batchId}/rows/${w.row}/email`, { recipient_email: email }));
  const keep = () => call(() => api.post(`/bulk/batches/${batchId}/rows/${w.row}/keep`));
  return (
    <li data-testid={`typo-row-${w.row}`} className="flex flex-wrap items-start gap-2 py-2 border-b last:border-0 border-amber-200/70 dark:border-amber-900/60">
      <span className="text-xs font-mono text-amber-800 dark:text-amber-300 pt-2.5 w-14">Row {w.row}</span>
      <div className="flex-1 min-w-[220px] space-y-1">
        <Input data-testid={`typo-row-input-${w.row}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-9 bg-white dark:bg-slate-900" disabled={!letter} />
        {letter ? <EmailTypoHint email={email} onFix={setEmail} testId={`typo-row-hint-${w.row}`} /> : <p className="text-xs text-amber-800">{w.message}</p>}
      </div>
      <button data-testid={`typo-row-save-${w.row}`} onClick={save} disabled={busy || !letter} className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-500 disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save</button>
      <button data-testid={`typo-row-keep-${w.row}`} onClick={keep} disabled={busy} className="h-9 px-4 rounded-full border border-amber-300 text-xs font-semibold text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50">Keep as is</button>
    </li>
  );
};

export const BatchTypoFix = ({ batch, letters, onChange }) => (
  <div data-testid="batch-email-warnings" className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-4 text-sm">
    <p className="font-semibold text-amber-800 dark:text-amber-300">{batch.contact_warnings.length} email address(es) may have a typo. Fix them here before you pay. There's no need to upload again.</p>
    <p className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-1">We only check spelling. Each change is recorded in the letter's history.</p>
    <ul className="mt-2">{batch.contact_warnings.map((w) => (
      <WarningRow key={w.row} batchId={batch.id} w={w} letter={letters.find((m) => m.csv_row === w.row)} onChange={onChange} />
    ))}</ul>
  </div>
);
